import { PurchasingGroupAssignableUser } from '../models/purchasing-group-assignable-user.model';
import { PurchasingGroupAssignment } from '../models/purchasing-group-assignment.model';
import { PurchasingGroupDetail } from '../models/purchasing-group-detail.model';
import { PurchasingGroupMember } from '../models/purchasing-group-member.model';

export interface ICompanyCodeGroup {
    readonly companyCodeId: string;
    readonly companyCode: string;
    readonly companyCodeName: string | null;
    readonly assignments: ReadonlyArray<PurchasingGroupAssignment>;
    /**
     * True if assignments exist for this company code but the company code is no longer on the header
     * level. The user can still remove the orphan assignments; adding new ones would be rejected by
     * the domain invariant (EPROC-10822), so the material-group dialog opens in remove-only mode for
     * these groups.
     */
    readonly isOrphan: boolean;
}

/**
 * Builds the per-company-code groups for the detail page tree.
 *
 * - For each company code on the purchasing group's header level, returns a group with its
 *   assignments (may be empty). Code and name come from the detail response, so header company codes
 *   without assignments still display correctly.
 * - Any assignment whose `companyCodeId` is NOT on the header level is surfaced as an "orphan"
 *   group so the user can clean it up; the material-group dialog opens in remove-only mode for orphans.
 * - Groups are sorted with header company codes first (by code, numeric-aware) and orphans appended
 *   at the end (also by code).
 */
export function buildCompanyCodeGroups(
    purchasingGroup: PurchasingGroupDetail,
    assignments: ReadonlyArray<PurchasingGroupAssignment>,
): ReadonlyArray<ICompanyCodeGroup> {
    const assignmentsByCc = new Map<string, PurchasingGroupAssignment[]>();
    for (const assignment of assignments) {
        const bucket = assignmentsByCc.get(assignment.companyCodeId) ?? [];
        bucket.push(assignment);
        assignmentsByCc.set(assignment.companyCodeId, bucket);
    }

    const seen = new Set<string>();
    const groups: ICompanyCodeGroup[] = [];
    for (const headerCompanyCode of purchasingGroup.assignedCompanyCodes) {
        seen.add(headerCompanyCode.id);
        groups.push({
            companyCodeId: headerCompanyCode.id,
            companyCode: headerCompanyCode.code,
            companyCodeName: headerCompanyCode.name,
            assignments: sortByMaterialGroupCode(assignmentsByCc.get(headerCompanyCode.id) ?? []),
            isOrphan: false,
        });
    }
    for (const [ccId, ccAssignments] of assignmentsByCc) {
        if (seen.has(ccId)) continue;
        groups.push({
            companyCodeId: ccId,
            companyCode: ccAssignments[0].companyCode,
            companyCodeName: ccAssignments[0].companyCodeName,
            assignments: sortByMaterialGroupCode(ccAssignments),
            isOrphan: true,
        });
    }
    groups.sort((a, b) => {
        if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
        return a.companyCode.localeCompare(b.companyCode, undefined, { numeric: true, sensitivity: 'base' });
    });
    return groups;
}

function sortByMaterialGroupCode(
    assignments: ReadonlyArray<PurchasingGroupAssignment>,
): ReadonlyArray<PurchasingGroupAssignment> {
    return [...assignments].sort((a, b) =>
        a.materialGroupCode.localeCompare(b.materialGroupCode, undefined, { numeric: true, sensitivity: 'base' }),
    );
}

/**
 * Builds the displayable members list from raw customer ids plus a lookup of known users.
 * For unknown ids the id itself is used as the display name and `active` stays unset (null) so the
 * UI knows it's an opaque reference rather than a confirmed-inactive user.
 */
export function buildMembers(
    customerIds: ReadonlyArray<string>,
    users: ReadonlyArray<PurchasingGroupAssignableUser>,
): ReadonlyArray<PurchasingGroupMember> {
    const usersById = new Map(users.map((user) => [user.id, user]));
    return customerIds
        .map((id) => {
            const user = usersById.get(id);
            return new PurchasingGroupMember(id, user?.valueDisplay ?? id, user?.active ?? null);
        })
        .sort((left, right) => compareLocalized(left.displayName, right.displayName));
}

/**
 * Returns the given users sorted by full name in German locale-aware order.
 */
export function sortAssignableUsersByName(
    users: ReadonlyArray<PurchasingGroupAssignableUser>,
): ReadonlyArray<PurchasingGroupAssignableUser> {
    return [...users].sort((left, right) => compareLocalized(left.fullName, right.fullName));
}

function compareLocalized(left: string, right: string): number {
    return left.localeCompare(right, 'de', { sensitivity: 'base' });
}
